import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { TRANSITION_CANCEL_BY_PROVIDER } from '../../util/transaction';
import * as log from '../../util/log';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchNumberOfPendingServiceFees } from '../../ducks/user.duck';

const { UUID } = sdkTypes;

// ================ Initial State ================ //

const initialState = {
  fetchServiceFeeInProgress: false,
  fetchServiceFeeError: null,
  transactionRef: null,
  cancelInProgress: false,
  cancelServiceFeeError: null,
  cancelServiceFeeSuccess: false,
  defineSuspiciousInProgress: false,
  defineSuspiciousError: null,
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
};

// ================ Async Thunks ================ //

export const fetchServiceFee = createAsyncThunk(
  'app/ServiceFeePage/fetchServiceFee',
  async ({ id, txRole }, { dispatch, rejectWithValue, extra: sdk }) => {
    try {
      const response = await sdk.newSdk.transactions.show(
        {
          id: id.uuid,
          include: [
            'user',
            'user.image',
            'provider',
            'provider.image',
            'listings',
            'deliveryToProvider',
            'listings.images',
            'relatedTransactions.listings.images',
          ],
        },
        { expand: true }
      );

      dispatch(
        addMarketplaceEntities({
          data: {
            data: {
              ...response.data.data,
              relationships: {
                ...response.data.data.relationships,
                relatedTransactions: {
                  data: response.data.data.relationships.relatedTransactions.data
                    .map(p => ({ ...p, type: 'related-transaction' }))
                    .filter(i => i.id.uuid !== id.uuid),
                },
              },
            },
            included: response.data.included
              .map(i => (i.type === 'transaction' ? { ...i, type: 'related-transaction' } : i))
              .filter(i => i.id.uuid !== id.uuid),
          },
        })
      );

      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const cancelServiceFee = createAsyncThunk(
  'app/ServiceFeePage/cancelServiceFee',
  async (data, { dispatch, rejectWithValue, extra: sdk }) => {
    const { id, reason } = data;
    try {
      const response = await sdk.newSdk.transactions.cancel({ id: id.uuid, reason });

      dispatch(addMarketplaceEntities(response));
      dispatch(fetchNumberOfPendingServiceFees());

      return response;
    } catch (e) {
      log.error(e, 'reject-sale-failed', {
        txId: id,
        transition: TRANSITION_CANCEL_BY_PROVIDER,
      });
      return rejectWithValue(storableError(e));
    }
  }
);

// ================ Slice ================ //

const serviceFeePageSlice = createSlice({
  name: 'app/ServiceFeePage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      const pickedState = pick(action.payload, Object.keys(initialState));
      return { ...initialState, ...pickedState };
    },
    cancelServiceFeeClear: state => {
      state.cancelServiceFeeSuccess = false;
      state.cancelInProgress = false;
      state.cancelServiceFeeError = null;
    },
  },
  extraReducers: builder => {
    builder
      // fetchServiceFee
      .addCase(fetchServiceFee.pending, state => {
        state.fetchServiceFeeInProgress = true;
        state.fetchServiceFeeError = null;
      })
      .addCase(fetchServiceFee.fulfilled, (state, action) => {
        state.fetchServiceFeeInProgress = false;
        state.transactionRef = {
          id: action.payload.data.data.id,
          type: 'transaction',
        };
      })
      .addCase(fetchServiceFee.rejected, (state, action) => {
        console.error(action.payload);
        state.fetchServiceFeeInProgress = false;
        state.fetchServiceFeeError = action.payload;
      })

      // cancelServiceFee
      .addCase(cancelServiceFee.pending, state => {
        state.cancelInProgress = true;
        state.cancelServiceFeeError = null;
        state.cancelServiceFeeSuccess = false;
      })
      .addCase(cancelServiceFee.fulfilled, state => {
        state.cancelInProgress = false;
        state.cancelServiceFeeSuccess = true;
      })
      .addCase(cancelServiceFee.rejected, (state, action) => {
        state.cancelInProgress = false;
        state.cancelServiceFeeError = action.payload;
      });
  },
});

// ================ Actions & Reducer Export ================ //

export const { setInitialValues, cancelServiceFeeClear } = serviceFeePageSlice.actions;

export default serviceFeePageSlice.reducer;

// ================ Helper Thunk (loadData) ================ //

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const loadData = params => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state?.transactionRef;
  const txRole = params.transactionRole;

  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  return dispatch(fetchServiceFee({ id: txId, txRole }));
};
